<script lang="ts" setup>
import { computed } from "vue";

import { toRelativeTime } from "@Helpers/support";
import XNotifications from "@Modules/shared/store/XNotifications";
import XOverlay from "@Modules/shared/store/XOverlay";
import { useLegacyStore, useTranslation } from "@Resources/js/composables";
import FormCheckbox from "@Resources/js/components/forms/FormCheckbox.vue";
import type { TypedNotification, TypedAnnouncement } from "@Resources/js/laravel";
import PopoverOverhauling from "@Resources/js/components/popover/PopoverOverhauling.vue";

/**
 * The popover lists notifications and announcements in one date-sorted feed.
 * Each row wraps the store's own notification or announcement rather than a
 * copy of it: marking a notification read flips the flag on that object, and a
 * spread copy would hide the change from Vue.
 */
type NotificationRow = { type: "notification"; item: TypedNotification };
type AnnouncementRow = { type: "announcement"; item: TypedAnnouncement };
type NotificationOrAnnouncement = NotificationRow | AnnouncementRow;

const store = useLegacyStore();
const { __ } = useTranslation();
const overlay = XOverlay();
const notificationsStore = XNotifications();

const isOnMobile = computed(() => store?.getters.isOnMobile);
const notifications = computed<TypedNotification[]>(() => store?.getters.notifications ?? []);
const announcements = computed<TypedAnnouncement[]>(() => store?.getters.announcements ?? []);
const combined = computed<NotificationOrAnnouncement[]>(() => {
  const retval: NotificationOrAnnouncement[] = [
    ...notifications.value.map((notification) => ({ type: "notification" as const, item: notification })),
    ...announcements.value.map((announcement) => ({ type: "announcement" as const, item: announcement })),
  ];

  return retval.sort((a, b) => new Date(b.item.created_at).getTime() - new Date(a.item.created_at).getTime());
});

function hasIcon(icon: string) {
  return icon.includes("fa-");
}
</script>

<template>
  <PopoverOverhauling @close="overlay.hide()">
    <template #title>
      {{ __("overlay.notifications.title") }}
    </template>

    <div
      v-if="combined.length"
      class="notif"
    >
      <template v-for="d in combined">
        <template v-if="d.type === 'notification'">
          <div
            :key="d.item.id"
            class="_notification_item notif-item"
            :class="{ opac: d.item.read }"
          >
            <div class="icon">
              <span class="flex-center-center h-100 w-100">
                <i
                  v-if="hasIcon(d.item.icon)"
                  :class="['fa-solid', 'fa-2x', 'fa-inverse', d.item.icon]"
                />
                <img
                  v-else
                  class="rounded-0"
                  :src="'/images/promos/bfcm2023/' + d.item.icon"
                >
              </span>
            </div>
            <div class="content">
              <div class="flex-start-between mt-1 mb-3">
                <div class="flex-center-between flex-wrap w-100">
                  <h5 class="font-weight-bold mb-0">
                    <span v-if="d.item.creator">{{ d.item.creator.name }}</span>
                    <span v-else>{{ __("tiny_url") }}</span>
                  </h5>
                  <div>{{ toRelativeTime(d.item.created_at) }}</div>
                </div>
                <FormCheckbox
                  v-if="d.item.read !== null"
                  :id="'ntf' + d.item.id"
                  v-tooltip="{
                    content: !d.item.read ? __('overlay.notifications.mark_as_read') : false,
                    trigger: isOnMobile ? 'click' : 'focus hover',
                  }"
                  tabindex="0"
                  :label="''"
                  :value="d.item.read"
                  :is-checked="d.item.read"
                  :is-disabled="d.item.read"
                  class="ml-3 mr-n2 w-auto _notif_read"
                  @change="notificationsStore.markNotificationAsRead(d.item)"
                />
              </div>
              <!-- eslint-disable vue/no-v-html -->
              <div
                class="body"
                v-html="d.item.parsed_body"
              />
              <template v-if="d.item.action_text">
                <button
                  v-if="d.item.action_type === 'verify_email'"
                  type="button"
                  class="btn btn-blue _verify_email"
                  style="min-width: 218px"
                  @click="overlay.showEmailVerifyPopover()"
                >
                  {{ d.item.action_text }}
                </button>

                <a
                  v-else-if="typeof d.item.action_url === 'string'"
                  :href="d.item.action_url"
                  :target="d.item.is_action_target_blank ? '_blank' : ''"
                  class="btn btn-blue"
                  style="min-width: 218px"
                >
                  {{ d.item.action_text }}
                </a>

                <router-link
                  v-else
                  :to="d.item.action_url"
                  class="btn btn-blue"
                  style="min-width: 218px"
                >
                  {{ d.item.action_text }}
                </router-link>
              </template>
            </div>
          </div>
        </template>
        <template v-if="d.type === 'announcement'">
          <div
            :key="d.item.id"
            class="_announcement_item notif-item"
          >
            <div class="icon">
              <img
                v-if="d.item.creator.photo_url"
                :src="d.item.creator.photo_url"
              >
            </div>
            <div class="content">
              <div class="flex-start-between mt-1 mb-3">
                <div class="flex-center-between flex-wrap w-100">
                  <h5 class="font-weight-bold mb-0">
                    {{ d.item.creator.name }}
                  </h5>
                  <div>{{ toRelativeTime(d.item.created_at) }}</div>
                </div>
              </div>
              <!-- eslint-disable vue/no-v-html -->
              <div
                class="body"
                v-html="d.item.parsed_body"
              />
              <template v-if="d.item.action_text">
                <a
                  v-if="typeof d.item.action_url === 'string'"
                  :href="d.item.action_url"
                  class="btn btn-blue"
                  style="min-width: 218px"
                >
                  {{ d.item.action_text }}
                </a>

                <router-link
                  v-else
                  :to="d.item.action_url"
                  class="btn btn-blue"
                  style="min-width: 218px"
                >
                  {{ d.item.action_text }}
                </router-link>
              </template>
            </div>
          </div>
        </template>
      </template>
    </div>

    <div
      v-else
      class="_empty_state alert alert-info alert-heading small mt-3 mx-3"
    >
      We don't have anything to show you right now! But when we do, we'll be
      sure to let you know. Talk to you soon!
    </div>
  </PopoverOverhauling>
</template>
